// Phase 90.C.1 — Floating global call modal (voice-only).
//
// Renders whenever CallContext state != idle. Handles:
//   • Incoming call popup (Accept / Reject)
//   • Outgoing call ringing (Cancel)
//   • Active call UI (mute toggle + hangup + call timer)

import React, { useEffect, useState } from "react";
import { Phone, PhoneOff, PhoneCall, Mic, MicOff, User, Volume2, Ear } from "lucide-react";
import { useCall } from "../../contexts/CallContext";

function formatDuration(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function Avatar({ peer }) {
  const src = peer?.avatar_url;
  const initial = (peer?.name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="relative w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-emerald-500/40 to-cyan-500/40 flex items-center justify-center border-2 border-white/15">
      {src ? (
        <img src={src} alt="peer" className="w-full h-full object-cover" />
      ) : (
        <User className="w-10 h-10 text-white/70" />
      )}
      <span className="absolute -top-1 -right-1 text-2xl">{initial}</span>
    </div>
  );
}

export default function CallModal() {
  const { phase, peer, muted, remoteConnected, audioBlocked, speakerOn, answerCall, rejectCall, endCall, toggleMute, toggleSpeaker, enableAudio } = useCall();
  const [secs, setSecs] = useState(0);
  // v16.73 — Nút loa ngoài/loa thoại chỉ có tác dụng trong app shell (native
  // điều hướng audio route được); web browser không kiểm soát được earpiece.
  const isNativeShell = typeof window !== "undefined" && window.__pickleagueNativeApp === true;

  useEffect(() => {
    if (phase !== "active" || !remoteConnected) {
      setSecs(0);
      return undefined;
    }
    const t = setInterval(() => setSecs((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [phase, remoteConnected]);

  if (phase === "idle") return null;

  const isIncoming = phase === "incoming";
  const isPreparing = phase === "preparing";
  const isOutgoing = phase === "outgoing";
  const isActive = phase === "active";
  const isEnding = phase === "ending";

  const name = peer?.name || (isIncoming ? "Cuộc gọi đến" : "Người dùng");
  const rolesLabel = (peer?.roles || []).join(", ");

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: "rgba(3,8,20,0.65)", backdropFilter: "blur(6px)" }}
      data-testid="call-modal-overlay"
    >
      <div
        className="w-full max-w-sm rounded-3xl p-6 flex flex-col items-center gap-4"
        style={{
          background: "linear-gradient(160deg, rgba(11,18,32,0.98) 0%, rgba(16,32,52,0.98) 100%)",
          border: "1px solid rgba(255,255,255,0.09)",
          boxShadow: "0 40px 120px rgba(0,0,0,0.7), 0 0 60px rgba(52,216,255,0.15)",
        }}
        data-testid="call-modal-card"
      >
        <div className="text-xs uppercase tracking-[0.24em] text-white/50 font-semibold" data-testid="call-modal-phase">
          {isIncoming && "Cuộc gọi đến"}
          {isPreparing && "Đang chuẩn bị cuộc gọi…"}
          {isOutgoing && "Đang gọi…"}
          {isActive && (remoteConnected ? "Đang trò chuyện" : "Đang kết nối…")}
          {isEnding && "Đang kết thúc…"}
        </div>

        <Avatar peer={peer} />

        <div className="text-center">
          <div className="text-xl font-bold text-white" data-testid="call-modal-peer-name">
            {name}
          </div>
          {rolesLabel && (
            <div className="text-xs text-white/60 mt-1 uppercase tracking-wider">{rolesLabel}</div>
          )}
          {isActive && remoteConnected && (
            <div className="text-emerald-400 font-mono text-sm mt-2" data-testid="call-modal-duration">
              {formatDuration(secs)}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4 mt-4">
          {isIncoming && (
            <>
              <button
                onClick={rejectCall}
                data-testid="call-modal-reject-btn"
                className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition shadow-lg shadow-red-500/40"
                aria-label="Từ chối"
              >
                <PhoneOff className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={answerCall}
                data-testid="call-modal-answer-btn"
                className="w-14 h-14 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center transition shadow-lg shadow-emerald-500/40 animate-pulse"
                aria-label="Trả lời"
              >
                <Phone className="w-6 h-6 text-white" />
              </button>
            </>
          )}

          {(isPreparing || isOutgoing) && (
            <button
              onClick={rejectCall}
              data-testid="call-modal-cancel-btn"
              className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition shadow-lg shadow-red-500/40"
              aria-label="Huỷ"
            >
              <PhoneOff className="w-6 h-6 text-white" />
            </button>
          )}

          {(isActive || isEnding) && (
            <>
              <button
                onClick={toggleMute}
                data-testid="call-modal-mute-btn"
                className={`w-12 h-12 rounded-full flex items-center justify-center transition ${muted ? "bg-white/20" : "bg-white/10 hover:bg-white/20"}`}
                aria-label={muted ? "Bật mic" : "Tắt mic"}
              >
                {muted ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5 text-white" />}
              </button>
              {isNativeShell && (
                <button
                  onClick={toggleSpeaker}
                  data-testid="call-modal-speaker-btn"
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition ${speakerOn ? "bg-white/25" : "bg-white/10 hover:bg-white/20"}`}
                  aria-label={speakerOn ? "Chuyển loa thoại trong" : "Bật loa ngoài"}
                  title={speakerOn ? "Loa ngoài" : "Loa thoại"}
                >
                  {speakerOn ? <Volume2 className="w-5 h-5 text-white" /> : <Ear className="w-5 h-5 text-white" />}
                </button>
              )}
              <button
                onClick={endCall}
                data-testid="call-modal-end-btn"
                className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition shadow-lg shadow-red-500/40"
                aria-label="Kết thúc"
              >
                <PhoneOff className="w-6 h-6 text-white" />
              </button>
            </>
          )}
        </div>

        {isActive && audioBlocked && (
          <button
            onClick={enableAudio}
            data-testid="call-modal-enable-audio-btn"
            className="mt-1 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500/20 border border-amber-400/50 text-amber-300 text-sm font-bold animate-pulse hover:bg-amber-500/30 transition"
          >
            <Volume2 className="w-4 h-4" /> Bấm để bật âm thanh
          </button>
        )}

        {(isPreparing || isOutgoing) && (
          <div className="mt-2 text-white/50 text-xs animate-pulse" data-testid="call-modal-outgoing-status">
            <PhoneCall className="w-3 h-3 inline mr-1" />
            {isPreparing ? "đang kết nối phòng thoại…" : "đang rung chuông…"}
          </div>
        )}
      </div>
    </div>
  );
}
